import { Icon, Input } from '@flatfile/baseline'
import { useMemo, useState } from 'react'

export interface FieldTypePanelProps {
  onBack: () => void
  onClose: () => void
  selectedType: string
  onSelectType: (type: string) => void
  fieldTypePanelRef: React.RefObject<HTMLDivElement>
}

export function FieldTypePanel({
  onBack,
  onClose,
  selectedType,
  onSelectType,
  fieldTypePanelRef,
}: FieldTypePanelProps) {
  const [searchQuery, setSearchQuery] = useState('')

  const smartFields = [
    { name: 'Smart fill', icon: 'obvious-list-sparkle' },
    { name: 'Web search', icon: 'obvious-globus' },
    { name: 'Translate', icon: 'obvious-translate' },
  ]

  const fields = [
    { name: 'Text', icon: 'obvious-bars-three-2' },
    { name: 'Number', icon: 'obvious-hashtag' },
    { name: 'Location', icon: 'obvious-map-pin' },
    { name: 'Date', icon: 'obvious-calendar-days' },
    { name: 'Attachment', icon: 'obvious-paperclip-2' },
    { name: 'Currency', icon: 'obvious-dollar' },
    { name: 'Rating', icon: 'obvious-star' },
    { name: 'Checkbox', icon: 'obvious-checkmark-2' },
    { name: 'Single-select', icon: 'obvious-circle-single-select' },
    { name: 'Multi-select', icon: 'obvious-list-sparkle' },
    { name: 'Email', icon: 'obvious-email-1' },
    { name: 'Phone', icon: 'obvious-telephone' },
    { name: 'Percentage', icon: 'obvious-percent' },
    { name: 'Url', icon: 'obvious-chain-link-3' },
  ]

  const filteredSmartFields = useMemo(() => {
    if (!searchQuery.trim()) return smartFields
    return smartFields.filter((field) =>
      field.name.toLowerCase().includes(searchQuery.toLowerCase())
    )
  }, [searchQuery, smartFields])

  const filteredFields = useMemo(() => {
    if (!searchQuery.trim()) return fields
    return fields.filter((field) =>
      field.name.toLowerCase().includes(searchQuery.toLowerCase())
    )
  }, [searchQuery, fields])

  const hasSmartFields = filteredSmartFields.length > 0
  const hasFields = filteredFields.length > 0

  return (
    <div ref={fieldTypePanelRef} className='pt-4 pb-0 min-h-fit flex flex-col'>
      {/* Header */}
      <div className='px-4 flex items-center justify-between mb-6'>
        <div className='flex items-center gap-3'>
          <button
            onClick={onBack}
            className='text-[#64748b] hover:text-[#1b1b1e]'
          >
            <Icon name='obvious-arrow-left' size={16} />
          </button>
          <span className='text-sm font-medium text-[#1b1b1e]'>
            Change field type
          </span>
        </div>
        <button
          onClick={onClose}
          className='text-[#64748b] hover:text-[#1b1b1e]'
        >
          <Icon name='obvious-x' size={16} />
        </button>
      </div>

      {/* Search input */}
      <div className='px-4 mb-6'>
        <Input
          type='text'
          placeholder='Search for field types'
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className='flex-1 text-sm text-[#1b1b1e] placeholder-[#838383] bg-[#f8f9fa] px-3'
          variant='ghost'
        />
      </div>

      <div className='overflow-y-auto h-[270px]'>
        {/* Smart fields section */}
        {hasSmartFields && (
          <div className='px-4 mb-6'>
            <h3 className='text-sm font-medium text-[#838383] mb-3'>
              Smart fields
            </h3>
            <div className='space-y-1'>
              {filteredSmartFields.map((field) => (
                <button
                  key={field.name}
                  onClick={() => onSelectType(field.name)}
                  className='w-full flex items-center justify-between px-3 py-2 hover:bg-[#e0e1e6] rounded-lg transition-colors'
                >
                  <div className='flex items-center gap-3'>
                    <Icon
                      name={field.icon}
                      size={16}
                      className='text-[#8e4ec6]'
                    />
                    <span className='text-sm text-[#1b1b1e]'>{field.name}</span>
                  </div>
                  {selectedType === field.name && (
                    <Icon
                      name='obvious-check'
                      size={16}
                      className='text-[#64748b]'
                    />
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Fields section */}
        {hasFields && (
          <div className='px-4'>
            <h3 className='text-sm font-medium text-[#838383] mb-3'>Fields</h3>
            <div className='space-y-1'>
              {filteredFields.map((field) => (
                <button
                  key={field.name}
                  onClick={() => onSelectType(field.name)}
                  className='w-full flex items-center justify-between px-3 py-2 hover:bg-[#e0e1e6] rounded-lg transition-colors'
                >
                  <div className='flex items-center gap-3'>
                    <Icon
                      name={field.icon}
                      size={16}
                      className='text-[#64748b]'
                    />
                    <span className='text-sm text-[#1b1b1e]'>{field.name}</span>
                  </div>
                  {selectedType === field.name && (
                    <Icon
                      name='obvious-check'
                      size={16}
                      className='text-[#64748b]'
                    />
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* No results message */}
        {!hasSmartFields && !hasFields && searchQuery.trim() && (
          <div className='px-4 py-8 text-center'>
            <p className='text-sm text-[#838383]'>
              No field types found matching "{searchQuery}"
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
